<template>
  <Teleport to="body">
    <div 
      v-if="hasNotifications"
      class="toast-container"
      :class="containerClass"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      <TransitionGroup
        name="toast-stack"
        tag="div"
        class="toast-container__stack"
      >
        <ToastNotification
          v-for="notification in visibleNotifications"
          :key="notification.id"
          :notification="notification"
          :auto-dismiss="getAutoDismissTime(notification)"
          :show-progress="showProgress"
          :position="position"
          @expand="handleExpand"
          @collapse="handleCollapse"
          @close="handleClose"
          @action="handleAction"
          @removed="handleRemoved"
          @timer-paused="handleTimerPaused"
          @timer-resumed="handleTimerResumed"
          class="toast-container__toast"
        />
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, nextTick } from 'vue';
import ToastNotification from './ToastNotification.vue';
import type { ToastNotification as ToastNotificationData } from '@/types/notifications';

export interface ToastContainerProps {
  /** Array of notifications to display */
  notifications: ToastNotificationData[];
  /** Position of the toast container */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  /** Maximum number of visible toasts */
  maxVisible?: number;
  /** Auto dismiss duration in milliseconds (can be overridden per notification) */
  autoDismiss?: number | false;
  /** Show progress indicator for auto dismiss */
  showProgress?: boolean;
  /** Spacing between toasts */
  spacing?: 'compact' | 'normal' | 'relaxed';
  /** Enable stacking animation */
  enableStacking?: boolean;
  /** Z-index for the container */
  zIndex?: number;
}

const props = withDefaults(defineProps<ToastContainerProps>(), {
  position: 'bottom-right',
  maxVisible: 5,
  autoDismiss: 5000,
  showProgress: true,
  spacing: 'normal',
  enableStacking: true,
  zIndex: 1000,
});

const emit = defineEmits<{
  expand: [id: string];
  collapse: [id: string];
  close: [id: string];
  action: [id: string, actionId: string];
  removed: [id: string];
  'timer-paused': [id: string];
  'timer-resumed': [id: string];
  'overflow-hidden': [hiddenCount: number];
  'displayed': [id: string]; // New event for when notification is displayed in toast
}>();

const hasNotifications = computed(() => {
  return props.notifications.length > 0;
});

const visibleNotifications = computed(() => {
  // Only show notifications that are marked as visible (not expired/hidden)
  const visibleOnly = props.notifications.filter(n => n.visible);
  
  // Sort by newest first
  const sortedByNewest = [...visibleOnly].sort((a, b) => {
    const aTime = a.createdAt?.getTime() ?? 0;
    const bTime = b.createdAt?.getTime() ?? 0;
    return bTime - aTime; // Newest first
  });
  
  const newestNotifications = sortedByNewest.slice(0, props.maxVisible);
  
  // Now sort these newest notifications for proper visual display
  const visible = newestNotifications.sort((a, b) => {
    const aTime = a.createdAt?.getTime() ?? 0;
    const bTime = b.createdAt?.getTime() ?? 0;
    
    if (props.position.includes('bottom')) {
      // For bottom positions: sort oldest first so newest appears at bottom (due to column-reverse)
      return aTime - bTime; 
    } else {
      // For top positions: sort newest first so newest appears at top
      return bTime - aTime;
    }
  });
  
  const hiddenCount = props.notifications.length - visible.length;
  
  if (hiddenCount > 0) {
    emit('overflow-hidden', hiddenCount);
  }
  
  return visible;
});

// Watch for newly visible notifications and mark them as displayed
watch(visibleNotifications, (newVisible, oldVisible) => {
  // Find notifications that are newly visible
  const previousIds = new Set(oldVisible?.map(n => n.id) || []);
  const newlyVisible = newVisible.filter(n => !previousIds.has(n.id));
  
  // Mark them as displayed and emit event
  nextTick(() => {
    newlyVisible.forEach(notification => {
      emit('displayed', notification.id);
    });
  });
});

const containerClass = computed(() => ({
  [`toast-container--${props.position}`]: true,
  [`toast-container--spacing-${props.spacing}`]: true,
  'toast-container--stacking': props.enableStacking,
}));

const getAutoDismissTime = (notification: ToastNotificationData): number | false => {
  // Sticky notifications never auto-dismiss
  if (notification.sticky) return false;
  
  // Use notification-specific timeout or default auto-dismiss
  return notification.timeout ?? props.autoDismiss;
};

const handleExpand = (id: string) => {
  emit('expand', id);
};

const handleCollapse = (id: string) => {
  emit('collapse', id);
};

const handleClose = (id: string) => {
  emit('close', id);
};

const handleAction = (id: string, actionId: string) => {
  emit('action', id, actionId);
};

const handleRemoved = (id: string) => {
  emit('removed', id);
};

const handleTimerPaused = (id: string) => {
  emit('timer-paused', id);
};

const handleTimerResumed = (id: string) => {
  emit('timer-resumed', id);
};
</script>

<style scoped lang="scss">
.toast-container {
  position: fixed;
  pointer-events: none;
  z-index: v-bind(zIndex);
  
  &__stack {
    display: flex;
    flex-direction: column;
    pointer-events: none;
  }
  
  &__toast {
    pointer-events: auto;
    transform-origin: center;
  }
  
  // Position variants
  &--top-right {
    top: var(--spacing-lg);
    right: var(--spacing-lg);
    
    .toast-container__stack {
      align-items: flex-end;
    }
  }
  
  &--top-left {
    top: var(--spacing-lg);
    left: var(--spacing-lg);
    
    .toast-container__stack {
      align-items: flex-start;
    }
  }
  
  &--bottom-right {
    bottom: calc(var(--spacing-lg) + var(--status-bar-height, 22px)); // Add status bar height
    right: var(--spacing-lg);
    
    .toast-container__stack {
      align-items: flex-end;
      flex-direction: column-reverse;
    }
  }
  
  &--bottom-left {
    bottom: calc(var(--spacing-lg) + var(--status-bar-height, 22px)); // Add status bar height
    left: var(--spacing-lg);
    
    .toast-container__stack {
      align-items: flex-start;
      flex-direction: column-reverse;
    }
  }
  
  &--top-center {
    top: var(--spacing-lg);
    left: 50%;
    transform: translateX(-50%);
    
    .toast-container__stack {
      align-items: center;
    }
  }
  
  &--bottom-center {
    bottom: calc(var(--spacing-lg) + var(--status-bar-height, 22px)); // Add status bar height
    left: 50%;
    transform: translateX(-50%);
    
    .toast-container__stack {
      align-items: center;
      flex-direction: column-reverse;
    }
  }
  
  // Spacing variants
  &--spacing-compact {
    .toast-container__toast:not(:last-child) {
      margin-bottom: var(--spacing-xs);
    }
    
    &.toast-container--bottom-right,
    &.toast-container--bottom-left,
    &.toast-container--bottom-center {
      .toast-container__toast:not(:last-child) {
        margin-bottom: 0;
        margin-top: var(--spacing-xs);
      }
    }
  }
  
  &--spacing-normal {
    .toast-container__toast:not(:last-child) {
      margin-bottom: var(--spacing-sm);
    }
    
    &.toast-container--bottom-right,
    &.toast-container--bottom-left,
    &.toast-container--bottom-center {
      .toast-container__toast:not(:last-child) {
        margin-bottom: 0;
        margin-top: var(--spacing-sm);
      }
    }
  }
  
  &--spacing-relaxed {
    .toast-container__toast:not(:last-child) {
      margin-bottom: var(--spacing-md);
    }
    
    &.toast-container--bottom-right,
    &.toast-container--bottom-left,
    &.toast-container--bottom-center {
      .toast-container__toast:not(:last-child) {
        margin-bottom: 0;
        margin-top: var(--spacing-md);
      }
    }
  }
  
  // Stacking effect (disabled)
  // Stacking effects were disabled to improve performance
  // Original stacking transforms can be re-enabled if needed
}

// Toast stack transition animations
.toast-stack-enter-active,
.toast-stack-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-stack-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.toast-stack-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.toast-stack-move {
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

// Responsive adjustments
@media (max-width: 480px) {
  .toast-container {
    &--top-right,
    &--top-left,
    &--bottom-right,
    &--bottom-left {
      left: var(--spacing-sm);
      right: var(--spacing-sm);
      top: var(--spacing-sm);
      
      .toast-container__stack {
        align-items: stretch;
      }
      
      .toast-container__toast {
        max-width: none;
        min-width: auto;
      }
    }
    
    &--bottom-right,
    &--bottom-left {
      top: auto;
      bottom: var(--spacing-sm);
    }
    
    &--top-center,
    &--bottom-center {
      left: var(--spacing-sm);
      right: var(--spacing-sm);
      transform: none;
      
      .toast-container__stack {
        align-items: stretch;
      }
    }
  }
}
</style>
